import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Search, X } from "lucide-react";
import AdminTopBar from "./AdminTopBar";
import AdminPagination from "./AdminPagination";
import { BusyTable } from "../components/TableBusyOverlay";
import usePagedListUi from "../hooks/usePagedListUi";
import { ADMIN_PAGE_SIZE, applyPagedSelect, totalPagesFromCount, uniqueIds } from "../lib/adminPagination";
import {
  adminCard,
  adminEmpty,
  adminPageBg,
  adminPageTitle,
  adminPrimaryBtn,
  adminRow,
  adminSearchInput,
  adminSearchWrap,
  adminStatusPill,
  adminTableHead,
  adminTableWrap,
  adminTd,
  adminTdName,
} from "./adminUi";

const PAGE_SIZE = ADMIN_PAGE_SIZE;
const RECENT_ORDERS_CAP = 8;

const buildOrderCode = (idValue) => {
  const source = String(idValue || "000").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  return `#ORD-${source.slice(0, 6).padEnd(3, "0")}`;
};

const toSecondKey = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value || "unknown");
  parsed.setMilliseconds(0);
  return parsed.toISOString();
};

const formatMoney = (value) => `PHP ${Number(value || 0).toLocaleString()}`;

const formatOrderDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleDateString("en-PH", {
    timeZone: "Asia/Manila",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const groupTicketOrders = (ticketsData, tiersData, eventsData) => {
  const tiersById = {};
  (tiersData || []).forEach((tier) => {
    tiersById[tier.id] = tier;
  });

  const eventsById = {};
  (eventsData || []).forEach((event) => {
    eventsById[event.id] = event;
  });

  const grouped = new Map();

  (ticketsData || []).forEach((ticket) => {
    const tier = tiersById[ticket.ticket_tier_id];
    const event = eventsById[tier?.event_id];
    const eventId = tier?.event_id || "unknown-event";
    const buyerId = ticket.buyer_id || "guest";
    const purchaseStamp = toSecondKey(ticket.created_at || ticket.id);
    const groupKey = `${buyerId}|${eventId}|${purchaseStamp}`;

    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, {
        id: groupKey,
        buyerId,
        orderCode: buildOrderCode(ticket.id),
        event: event?.title || "Unknown Event",
        eventDate: event?.date || "",
        purchaseDate: ticket.created_at,
        quantity: 0,
        total: 0,
      });
    }

    const row = grouped.get(groupKey);
    row.quantity += 1;
    row.total += Number(tier?.price || 0);
  });

  return Array.from(grouped.values()).sort((a, b) => {
    const aTime = a.purchaseDate ? new Date(a.purchaseDate).getTime() : 0;
    const bTime = b.purchaseDate ? new Date(b.purchaseDate).getTime() : 0;
    return bTime - aTime;
  });
};

const attachOrdersToCustomers = (users, orders) => {
  const ordersByBuyer = new Map();

  (orders || []).forEach((order) => {
    if (!order.buyerId || order.buyerId === "guest") return;
    if (!ordersByBuyer.has(order.buyerId)) {
      ordersByBuyer.set(order.buyerId, []);
    }
    ordersByBuyer.get(order.buyerId).push(order);
  });

  return (users || []).map((user) => {
    const recentOrders = ordersByBuyer.get(user.id) || [];
    return {
      ...user,
      totalOrders: recentOrders.length,
      recentOrders: recentOrders.slice(0, RECENT_ORDERS_CAP),
    };
  });
};

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [ordersWarning, setOrdersWarning] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const listTopRef = useRef(null);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    const needle = search.trim();
    const applyUserFilters = (query) => {
      let next = query.eq("role", "customer");
      if (statusFilter !== "All") {
        next = next.eq("status", statusFilter);
      }
      if (needle) {
        next = next.or(`full_name.ilike.%${needle}%,email.ilike.%${needle}%`);
      }
      return applyPagedSelect(next.order("created_at", { ascending: false }), currentPage, PAGE_SIZE);
    };

    let usersResult = await applyUserFilters(
      supabase.from("users").select("id, full_name, email, role, status, created_at", { count: "exact" })
    );
    if (usersResult.error) {
      usersResult = await applyUserFilters(
        supabase.from("users").select("*", { count: "exact" })
      );
    }

    const { data: usersData, error: usersError, count } = usersResult;

    if (usersError) {
      console.error("Error fetching customers:", usersError);
      setCustomers([]);
      setTotalCount(0);
      setLoadError(`Could not load customers. ${usersError.message || "Check that you can read users."}`);
      setOrdersWarning("");
      setLoading(false);
      return;
    }

    const customerUsers = usersData || [];
    const buyerIds = uniqueIds(customerUsers, "id");

    const { data: ticketsData, error: ticketsError } = buyerIds.length
      ? await supabase.from("tickets").select("id, buyer_id, ticket_tier_id, created_at").in("buyer_id", buyerIds)
      : { data: [], error: null };
    const neededTierIds = uniqueIds(ticketsData, "ticket_tier_id");
    const { data: tiersData, error: tiersError } = neededTierIds.length
      ? await supabase.from("ticket_tiers").select("id, event_id, name, price").in("id", neededTierIds)
      : { data: [], error: null };
    const neededEventIds = uniqueIds(tiersData, "event_id");
    const { data: eventsData, error: eventsError } = neededEventIds.length
      ? await supabase.from("events").select("id, title, date").in("id", neededEventIds)
      : { data: [], error: null };

    const ordersBlocked = ticketsError || tiersError || eventsError;
    if (ordersBlocked) {
      console.warn("Order history unavailable:", ticketsError || tiersError || eventsError);
      setOrdersWarning("Order history is unavailable. Customer list is shown with order counts at 0.");
      setCustomers(attachOrdersToCustomers(customerUsers, []));
    } else {
      setOrdersWarning("");
      const groupedOrders = groupTicketOrders(ticketsData, tiersData, eventsData);
      setCustomers(attachOrdersToCustomers(customerUsers, groupedOrders));
    }

    setTotalCount(Number(count) || customerUsers.length);
    setLoadError("");
    setLoading(false);
  }, [currentPage, search, statusFilter]);

  useEffect(() => {
    fetchCustomers();

    const channel = supabase
      .channel("admin-customers-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "users" }, fetchCustomers)
      .on("postgres_changes", { event: "*", schema: "public", table: "tickets" }, fetchCustomers)
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [fetchCustomers]);

  const totalPages = totalPagesFromCount(totalCount, PAGE_SIZE);
  const paginatedCustomers = customers;
  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  useEffect(() => {
    if (!selectedCustomer) return;
    const next = customers.find((customer) => customer.id === selectedCustomer.id);
    if (next) {
      setSelectedCustomer(next);
    }
  }, [customers, selectedCustomer]);

  const emptyMessage = loading && customers.length === 0
    ? "Loading..."
    : customers.length === 0
    ? "No customers yet."
    : "No customers match this search or status filter.";

  return (
    <div className={adminPageBg}>
      <AdminTopBar />

      <div className="p-4 sm:p-6 md:p-10">
        <div className="max-w-7xl mx-auto min-w-0">

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
          <h1 className={adminPageTitle}>
            Customers
          </h1>

          <div className={adminSearchWrap}>
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={adminSearchInput}
            />
          </div>
        </div>

        {loadError ? (
          <p className="mb-4 text-sm text-red-600">{loadError}</p>
        ) : null}
        {ordersWarning ? (
          <p className="mb-4 text-sm text-amber-700">{ordersWarning}</p>
        ) : null}

        <div className={adminCard} ref={listTopRef}>
        <BusyTable show={showSkeleton} tone="admin">
        <div className={adminTableWrap}>
          <table className="w-full min-w-[860px] text-left border-collapse">
            <thead className={adminTableHead}>
              <tr>
                <th className="px-4 py-3">Customer Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3 text-center">Total Orders</th>

                <th className="px-4 py-3 text-center relative">
                  <div className="inline-flex items-center gap-1 relative">
                    <span>Status</span>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="
                        absolute inset-0 opacity-0 cursor-pointer text-gray-700
                      "
                    >
                      <option value="All">All</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                    <span className="text-white">▾</span>
                  </div>
                </th>

                <th className="px-4 py-3 text-center">View</th>
              </tr>
            </thead>

            <tbody>
              {paginatedCustomers.length === 0 ? (
                <tr>
                  <td colSpan="5" className={adminEmpty}>
                    {emptyMessage}
                  </td>
                </tr>
              ) : paginatedCustomers.map((customer) => (
                <tr
                  key={customer.id}
                  className={adminRow}
                >
                  <td className={adminTdName}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">
                        {customer.full_name?.charAt(0).toUpperCase()}
                      </div>
                      {customer.full_name || "Unknown"}
                    </div>
                  </td>

                  <td className={adminTd}>
                    {customer.email}
                  </td>

                  <td className="px-4 py-4 text-center text-sm text-gray-600">
                    {customer.totalOrders || 0}
                  </td>

                  <td className="px-4 py-4 text-center">
                    <span
                      className={`${adminStatusPill} ${
                        (customer.status || "Active") === "Inactive"
                          ? "bg-red-100 text-red-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {customer.status || "Active"}
                    </span>
                  </td>

                  <td className="px-4 py-4 text-center">
                    <button
                      type="button"
                      onClick={() => setSelectedCustomer(customer)}
                      className={adminPrimaryBtn}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </BusyTable>

        <AdminPagination
          className="mt-6"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
        />
        </div>
        </div>
      </div>

      {selectedCustomer ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto rounded-xl shadow-lg relative p-6">

            <button
              type="button"
              onClick={() => setSelectedCustomer(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>

            <h2 className="text-sm font-semibold mb-4 text-gray-900 pr-8">
              Customer Details: {selectedCustomer.full_name || "Unknown"}
            </h2>

            <div className="flex flex-col items-center mb-6">
              <div className="w-14 h-14 rounded-full bg-blue-500 text-white flex items-center justify-center text-lg font-bold mb-2">
                {selectedCustomer.full_name?.charAt(0).toUpperCase() || "C"}
              </div>
              <p className="font-semibold text-gray-900">
                {selectedCustomer.full_name || "Unknown"}
              </p>
            </div>

            <div className="space-y-3 text-sm">
              <Detail label="Full Name" value={selectedCustomer.full_name || "-"} />
              <Detail label="Email" value={selectedCustomer.email || "-"} />
              <Detail label="Phone" value={selectedCustomer.phone || "-"} />
              <Detail label="Total Orders" value={selectedCustomer.totalOrders || 0} />
              <Detail label="Status" value={selectedCustomer.status || "Active"} />
            </div>

            <div className="mt-6">
              <h3 className="text-xs font-semibold mb-2 text-gray-700">
                Recent Orders
              </h3>
              {(selectedCustomer.recentOrders || []).length === 0 ? (
                <div className="bg-gray-100 rounded text-xs p-2 text-gray-500">
                  No orders yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedCustomer.recentOrders.map((order) => (
                    <div
                      key={order.id}
                      className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                    >
                      <p className="text-xs font-semibold text-gray-900">
                        {order.orderCode} · {order.event}
                      </p>
                      <p className="mt-1 text-[11px] text-gray-500">
                        {formatOrderDate(order.purchaseDate || order.eventDate)} · {order.quantity} ticket{order.quantity === 1 ? "" : "s"} · {formatMoney(order.total)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => navigate("/admin/orders")}
              className="mt-6 w-full bg-black text-white text-xs px-4 py-2 rounded-md hover:bg-gray-800"
            >
              Open Orders
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-400 mb-1">{label}</p>
    <input
      value={value}
      disabled
      className="w-full border rounded px-2 py-1 text-sm bg-gray-100 text-gray-800"
    />
  </div>
);

export default Customers;
